import React, { useContext } from 'react';
import {
  BrowserRouter as Router,
  Switch,
  Route,
  Redirect,
} from 'react-router-dom';
import {
  CookieConsent,
  Dashboard,
  Login,
  Notification,
  Payment,
  PaymentFailure,
  PaymentSuccess,
  PlanSelector,
  ProtectedRoute,
  UserRegistration,
} from './components';
import useAppConfig from './hooks/useAppConfig';
import { ThemeProvider } from 'emotion-theming';
import { AppContainer } from './components';
import { appStore } from './state';
import { isEmpty } from './utils';
import ForgottenPassword from './components/screens/ForgottenPassword/ForgottenPassword';
import { ROUTES } from './types';

function App() {
  const { loading, mixpanel } = useAppConfig(window.location);
  const { dispatch, state } = useContext(appStore);
  const { config, form, messages, user } = state;

  if (loading) {
    return <main>Loading...</main>;
  }

  const { params, translations } = state;

  return (
    <ThemeProvider theme={config.theme}>
      <Router>
        <AppContainer>
          <Switch>
            <ProtectedRoute
              exact
              path={ROUTES.DASHBOARD}
              redirect={[
                {
                  state: { nextPath: ROUTES.DASHBOARD },
                  shouldRedirect: !user.id,
                  to: ROUTES.USER_LOGIN,
                },
              ]}>
              <Dashboard
                localeCopy={
                  isEmpty(translations[params.locale])
                    ? undefined
                    : {
                        ...translations[params.locale]?.pages?.dashboard,
                        ...translations[params.locale]?.site,
                      }
                }
                fallbackCopy={
                  isEmpty(translations.fallback)
                    ? undefined
                    : {
                        ...translations.fallback.pages.dashboard,
                        ...translations.fallback.site,
                      }
                }
              />
            </ProtectedRoute>
            <Route exact path={ROUTES.PLAN_SELECTOR}>
              <PlanSelector
                localeCopy={
                  isEmpty(translations[params.locale])
                    ? undefined
                    : {
                        ...translations[params.locale]?.pages?.planSelector,
                        ...translations[params.locale]?.site,
                      }
                }
                fallbackCopy={
                  isEmpty(translations.fallback)
                    ? undefined
                    : {
                        ...translations.fallback.pages.planSelector,
                        ...translations.fallback.site,
                      }
                }
                mixpanel={mixpanel}
              />
            </Route>
            <Route exact path={ROUTES.USER_LOGIN}>
              <Login
                localeCopy={
                  isEmpty(translations[params.locale])
                    ? undefined
                    : {
                        ...translations[params.locale]?.pages?.login,
                        ...translations[params.locale]?.site,
                      }
                }
                fallbackCopy={
                  isEmpty(translations.fallback)
                    ? undefined
                    : {
                        ...translations.fallback.pages.login,
                        ...translations.fallback.site,
                      }
                }
              />
            </Route>
            <ProtectedRoute
              exact
              path={ROUTES.USER_REGISTRATION}
              redirect={[
                {
                  shouldRedirect: !form.selectedPlan,
                  to: ROUTES.PLAN_SELECTOR,
                },
                {
                  shouldRedirect: !!user?.apiKey,
                  to: ROUTES.PAYMENT,
                },
              ]}>
              <UserRegistration
                localeCopy={
                  isEmpty(translations[params.locale])
                    ? undefined
                    : {
                        ...translations[params.locale]?.pages?.userRegistration,
                        ...translations[params.locale]?.site,
                      }
                }
                fallbackCopy={
                  isEmpty(translations.fallback)
                    ? undefined
                    : {
                        ...translations.fallback.pages.userRegistration,
                        ...translations.fallback.site,
                      }
                }
              />
            </ProtectedRoute>
            <ProtectedRoute
              exact
              path={ROUTES.PAYMENT}
              redirect={[
                {
                  shouldRedirect: isEmpty(form.selectedPlan),
                  to: ROUTES.PLAN_SELECTOR,
                },
                { shouldRedirect: !user.id, to: ROUTES.USER_LOGIN },
              ]}>
              <Payment
                localeCopy={
                  isEmpty(translations[params.locale])
                    ? undefined
                    : {
                        ...translations[params.locale]?.pages?.payment,
                        ...translations[params.locale]?.site,
                      }
                }
                fallbackCopy={
                  isEmpty(translations.fallback)
                    ? undefined
                    : {
                        ...translations.fallback.pages.payment,
                        ...translations.fallback.site,
                      }
                }
              />
            </ProtectedRoute>
            <ProtectedRoute
              exact
              path={ROUTES.PAYMENT_SUCCESS}
              redirect={[
                {
                  shouldRedirect: !!!user.id,
                  to: ROUTES.PLAN_SELECTOR,
                },
              ]}>
              <PaymentSuccess
                localeCopy={
                  isEmpty(translations[params.locale])
                    ? undefined
                    : {
                        ...translations[params.locale]?.pages?.paymentSuccess,
                        ...translations[params.locale]?.site,
                      }
                }
                fallbackCopy={
                  isEmpty(translations.fallback)
                    ? undefined
                    : {
                        ...translations.fallback.pages.paymentSuccess,
                        ...translations.fallback.site,
                      }
                }
              />
            </ProtectedRoute>
            <Route exact path={ROUTES.USER_FORGOTTEN_PASSWORD}>
              <ForgottenPassword
                localeCopy={
                  isEmpty(translations[params.locale])
                    ? undefined
                    : {
                        ...translations[params.locale]?.pages
                          ?.forgottenPassword,
                        ...translations[params.locale]?.site,
                      }
                }
                fallbackCopy={
                  isEmpty(translations.fallback)
                    ? undefined
                    : {
                        ...translations.fallback.pages.forgottenPassword,
                        ...translations.fallback.site,
                      }
                }
              />
            </Route>
            <Route exact path={ROUTES.PAYMENT_FAILURE}>
              <PaymentFailure
                localeCopy={
                  isEmpty(translations[params.locale])
                    ? undefined
                    : {
                        ...translations[params.locale]?.site,
                      }
                }
                fallbackCopy={
                  isEmpty(translations.fallback)
                    ? undefined
                    : {
                        ...translations.fallback.site,
                      }
                }
              />
            </Route>
            <Route path='*'>
              <Redirect to={ROUTES.DASHBOARD} />
            </Route>
          </Switch>
        </AppContainer>
      </Router>
      <Notification dispatch={dispatch} messages={messages} />
      <CookieConsent 
        localeCopy={
          isEmpty(translations[params.locale])
            ? undefined
            : {
                ...translations[params.locale]?.site,
              }
        }
        fallbackCopy={
          isEmpty(translations.fallback)
            ? undefined
            : {
                ...translations.fallback.site,
              }
        }
        mixpanel={mixpanel}/>
    </ThemeProvider>
  );
}

export default App;
